import * as Sentry from '@sentry/react'

type MetaValue = string | number | boolean | null | undefined

export type ExceptionMetaInput = Record<
  string,
  string | number | null | undefined
>

/** `action` maps to Sentry's `message` and `metadata` to its `data`. */
export type BreadcrumbInput = {
  action: string
  category?: string
  metadata?: Record<string, MetaValue>
}

/**
 * The single seam between application code and the frontend error-reporting vendor, mirroring
 * `ErrorReporter` on the Rails side. Import `exceptions` rather than `@sentry/react` directly.
 *
 * Sentry's default integrations already cover console and network breadcrumbs, `window.onerror` /
 * `unhandledrejection` capture, and URL decoration, so there is nothing extra to register.
 */
export class Exceptions {
  private initialized = false

  /** Idempotent. Reporting before this runs is safe — captures no-op until initialisation. */
  public setup() {
    if (this.initialized || typeof window === 'undefined') return

    const dsn = process.env.SENTRY_FRONTEND_DSN
    if (!dsn || process.env.NODE_ENV === 'test') return

    Sentry.init({
      dsn,
      release: process.env.HEROKU_SLUG_COMMIT,

      // Sentry silently defaults a missing environment to "production", which would file staging
      // errors against production. A wrong-looking environment is easier to spot than that.
      environment: process.env.SENTRY_ENVIRONMENT || 'unspecified',

      sendDefaultPii: false,

      // Resolved at send time, not init, because the MobX store is usually not populated yet.
      beforeSend: (event) => {
        event.tags = { ...event.tags, ...this.metaTags() }

        const userId = window.store?.currentUser?.id
        if (userId) event.user = { ...event.user, id: String(userId) }

        return event
      },
    })

    this.initialized = true
  }

  public handle(
    error: Error,
    {
      params,
      tags,
    }: { params?: ExceptionMetaInput; tags?: ExceptionMetaInput } = {},
  ) {
    if (window.store?.debug) {
      console.error(error, { params })
    }

    Sentry.captureException(error, {
      extra: params,
      tags,
    })
  }

  public handleCustom(
    name: string,
    {
      message,
      ...meta
    }: {
      message?: string
      params?: ExceptionMetaInput
      tags?: ExceptionMetaInput
    } = {},
  ) {
    const error = new Error(message)
    error.name = name
    this.handle(error, meta)

    return error
  }

  public addBreadcrumb({ action, category, metadata }: BreadcrumbInput) {
    Sentry.addBreadcrumb({
      message: action,
      category,
      data: metadata,
    })
  }

  private metaTags() {
    const tags: Record<string, string> = {}
    const userId = window.store?.currentUser?.id
    const orgId = window.store?.currentUser?.org?.id
    const smartlook = window.smartlook?.sessionId

    if (userId) tags['user_id'] = String(userId)
    if (orgId) tags['org_id'] = String(orgId)
    if (smartlook) tags['smartlook_session_id'] = smartlook
    return tags
  }
}

export const exceptions = new Exceptions()
